import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Car,
  Trash2,
  Edit2,
  Calendar,
  Gauge,
  Zap,
  AlertTriangle,
  Cog,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../hooks/redux/useRedux";
import { selectUserCurrency } from "../../../redux/slices/profileSlice/ProfileSlice";
import { formatCurrency } from "../../../utils/currency/formatCurrency";
import { PrimaryButton } from "../../../components/buttons/primaryButton/PrimaryButton";

function DeleteVehicleModal({ isOpen, onClose, onConfirm, vehicleName }) {
  const { t } = useTranslation();
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[110] px-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t("profile.deleteVehicleTitle") || "Delete Vehicle"}
              </h3>

              <p className="text-gray-600 mb-6 text-sm">
                {t("profile.deleteVehiclePrompt") ||
                  `Are you sure you want to delete ${vehicleName || "this vehicle"}? This action cannot be undone.`}
              </p>

              <div className="flex gap-3 w-full">
                <PrimaryButton
                  onClick={onClose}
                  variant="outline"
                  size="small"
                  className="flex-1"
                  label={t("home.cancel")}
                />
                <PrimaryButton
                  onClick={onConfirm}
                  size="small"
                  className="flex-1 bg-red-500 hover:bg-red-600"
                  label={t("home.delete")}
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function VehicleCard({
  item,
  onEdit,
  onDelete,
  userType,
}) {
  const { t } = useTranslation();
  const userCurrency = useAppSelector(selectUserCurrency);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageError, setImageError] = useState(false);

  const title =
    `${item?.make_name || ""} ${item?.model_name || ""}`.trim() ||
    (item?.year ? `${item.year} Vehicle` : "Vehicle");

  const badgeText = (item?.body_type || item?.type || "SUV").toUpperCase();

  const formattedPrice = formatCurrency({
    number: item?.price || 0,
    currency: userCurrency,
  });

  const heroImage =
    !imageError &&
    (item?.first_image ||
      (Array.isArray(item?.exterior_images) &&
        item.exterior_images[0]?.image) ||
      (Array.isArray(item?.images) && item.images[0]?.image) ||
      null);

  const handleDeleteConfirm = () => {
    setShowDeleteModal(false);
    if (onDelete && item?.id) {
      onDelete(item.id);
    }
  };

  return (
    <>
      <motion.div
        whileHover={{ y: -6, boxShadow: "0 20px 35px -10px rgba(2, 6, 55,0.15)" }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
        className="relative bg-white rounded-3xl p-5 border border-gray-100 hover:border-[#020637]/30 flex flex-col h-full justify-between shadow-sm transition-all"
      >
        <div>
          {/* Top Hero Image Container */}
          <div className="relative w-full h-48 rounded-2xl overflow-hidden bg-gray-100 mb-4 group">
            {heroImage ? (
              <img
                src={heroImage}
                alt={title}
                onError={() => setImageError(true)}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-2 bg-gradient-to-br from-gray-50 to-gray-100">
                <Car className="w-10 h-10 stroke-[1.5]" />
                <span className="text-xs font-medium text-gray-500">
                  {t("profile.noImage") || "No Image"}
                </span>
              </div>
            )}

            {/* Badges Overlay */}
            <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
              {item?.is_demo && (
                <span className="bg-amber-500/90 text-white backdrop-blur-md text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-sm">
                  Demo
                </span>
              )}
              <span className="bg-black/60 text-white backdrop-blur-md text-[11px] font-semibold px-3 py-1 rounded-full uppercase tracking-wider shadow-sm">
                {badgeText}
              </span>
            </div>

           
          </div>

          {/* Title & Price Row */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3
              className="text-base sm:text-lg font-bold text-gray-900 line-clamp-1 flex-1 min-w-0"
              title={title}
            >
              {title}
            </h3>

            {userType !== "worker" && (
              <button
                onClick={() => setShowDeleteModal(true)}
                className="text-gray-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                title={t("home.delete")}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-xl sm:text-2xl font-bold text-gray-900">
              {formattedPrice}
            </span>
            {item?.leasing_price && (
              <span className="text-xs text-gray-500 font-medium">
                ({formatCurrency({ number: item.leasing_price, currency: userCurrency })}/mo)
              </span>
            )}
          </div>

          {/* Specs / Attributes Pills */}
          <div className="grid grid-cols-2 gap-2 mb-4 text-xs text-gray-600">
            {item?.year && (
              <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2.5 py-1.5 min-w-0">
                <Calendar className="w-3.5 h-3.5 text-[#020637] flex-shrink-0" />
                <span className="font-medium text-gray-700 truncate">{item.year}</span>
              </div>
            )}
            {item?.mileage !== undefined && item?.mileage !== null && (
              <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2.5 py-1.5 min-w-0">
                <Gauge className="w-3.5 h-3.5 text-[#020637] flex-shrink-0" />
                <span className="font-medium text-gray-700 truncate">
                  {Number(item.mileage).toLocaleString()} KM
                </span>
              </div>
            )}
            {item?.transmission_type && (
              <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2.5 py-1.5 min-w-0">
                <Cog className="w-3.5 h-3.5 text-[#020637] flex-shrink-0" />
                <span className="font-medium text-gray-700 capitalize truncate">
                  {item.transmission_type}
                </span>
              </div>
            )}
            {item?.engine && (
              <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2.5 py-1.5 min-w-0">
                <Zap className="w-3.5 h-3.5 text-[#020637] flex-shrink-0" />
                <span className="font-medium text-gray-700 capitalize truncate">
                  {item.engine}
                </span>
              </div>
            )}
          </div>

          {item?.description && (
            <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-4">
              {item.description}
            </p>
          )}
        </div>

        {/* Bottom Actions */}
        {userType !== "worker" && (
          <div className="pt-2 border-t border-gray-100 flex items-center gap-2 mt-auto">
            <button
              onClick={() => onEdit && onEdit(item)}
              className="flex-1 py-2.5 px-4 rounded-xl bg-gradient-to-r from-[#020637] to-[#1e3aa0] hover:from-[#0e1c7a] hover:to-[#1a2a8a] text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-sm transition-all"
            >
              <Edit2 className="w-4 h-4" />
              {t("profile.editVehicle") || "Edit Vehicle"}
            </button>
          </div>
        )}
      </motion.div>

      <DeleteVehicleModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        vehicleName={title}
      />
    </>
  );
}

export default VehicleCard;
